import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaEllipsisH } from 'react-icons/fa';
import './ActionMenu.css';

const MENU_GAP = 8;
const VIEWPORT_PADDING = 8;
const DEFAULT_PANEL_WIDTH = 220;

const ActionMenu = ({
  label = 'Actions',
  items = [],
  align = 'right',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const menuRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const visibleItems = items.filter(Boolean);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelStyle(null);
      return undefined;
    }

    const updatePanelPosition = () => {
      if (!triggerRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const panelWidth = panelRef.current?.offsetWidth || DEFAULT_PANEL_WIDTH;
      const panelHeight = panelRef.current?.offsetHeight || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const rawLeft = align === 'left' ? triggerRect.left : triggerRect.right - panelWidth;
      const left = Math.min(
        Math.max(VIEWPORT_PADDING, rawLeft),
        Math.max(VIEWPORT_PADDING, viewportWidth - panelWidth - VIEWPORT_PADDING),
      );

      const preferredTop = triggerRect.bottom + MENU_GAP;
      const shouldOpenAbove = panelHeight && preferredTop + panelHeight > viewportHeight - VIEWPORT_PADDING;
      const top = shouldOpenAbove
        ? Math.max(VIEWPORT_PADDING, triggerRect.top - panelHeight - MENU_GAP)
        : preferredTop;

      setPanelStyle({ left, top });
    };

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [align, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const isInsideTrigger = menuRef.current && menuRef.current.contains(event.target);
      const isInsidePanel = panelRef.current && panelRef.current.contains(event.target);

      if (!isInsideTrigger && !isInsidePanel) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (visibleItems.length === 0) {
    return null;
  }

  const handleItemClick = (item) => {
    if (item.disabled) return;
    setIsOpen(false);
    item.onClick?.();
  };

  const panel = isOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        className={`ui-action-menu-panel ui-action-menu-${align}`}
        ref={panelRef}
        role="menu"
        style={{
          left: panelStyle ? `${panelStyle.left}px` : undefined,
          top: panelStyle ? `${panelStyle.top}px` : undefined,
          visibility: panelStyle ? 'visible' : 'hidden',
        }}
      >
        {visibleItems.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            className={`ui-action-menu-item ${item.tone ? `ui-action-menu-item-${item.tone}` : ''}`.trim()}
            disabled={item.disabled}
            onClick={() => handleItemClick(item)}
          >
            {item.icon && <span className="ui-action-menu-icon">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))}
      </div>,
      document.body,
    )
    : null;

  return (
    <div className={`ui-action-menu ${isOpen ? 'ui-action-menu-open' : ''} ${className}`.trim()} ref={menuRef}>
      <button
        type="button"
        className="ui-action-menu-trigger"
        ref={triggerRef}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <FaEllipsisH aria-hidden="true" />
      </button>
      {panel}
    </div>
  );
};

export default ActionMenu;
